import React, { useState, useRef, useEffect } from "react";
import {
  Copy,
  Maximize2,
  Minimize2,
  Check,
  Download,
  Clipboard,
  ClipboardCheck,
} from "lucide-react";
import CodeMirror from "@uiw/react-codemirror";
import { html } from "@codemirror/lang-html";
import { oneDark } from "@codemirror/theme-one-dark";

interface CodeOutputProps {
  code: string;
  onChange: (newCode: string) => void;
  onCopy?: () => void;
  hideControls?: boolean;
}

export function CodeOutput({
  code,
  onChange,
  onCopy,
  hideControls = false,
}: CodeOutputProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isExpanded) {
        e.preventDefault();
        setIsExpanded(false);
      }
    };

    const prevOverflow = document.body.style.overflow;

    if (isExpanded) {
      document.addEventListener("keydown", handleEsc);
      document.body.style.overflow = "hidden";

      return () => {
        document.removeEventListener("keydown", handleEsc);
        document.body.style.overflow = prevOverflow;
      };
    }
  }, [isExpanded]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setIsCopied(true);
      console.log("Code copied to clipboard");

      if (onCopy) {
        onCopy();
      }

      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy code:", err);

      try {
        const textArea = document.createElement("textarea");
        textArea.value = code;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);

        setIsCopied(true);
        if (onCopy) {
          onCopy();
        }
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      } catch (fallbackErr) {
        console.error("Fallback copy failed:", fallbackErr);
      }
    }
  };

  return (
    <div
      className={`relative ${
        isExpanded
          ? "fixed inset-0 z-50 bg-volt-deep-blue/95 backdrop-blur-sm p-4 md:p-8 flex items-center justify-center overflow-hidden"
          : "h-full flex flex-col"
      }`}
    >
      <div
        className={`relative rounded-lg border border-gray-700 bg-volt-carbon overflow-hidden ${
          isExpanded
            ? "w-[95vw] h-[90vh] max-w-[1400px] mx-auto"
            : "w-full flex-1"
        }`}
      >
        <CodeMirror
          value={code}
          height={isExpanded ? "100%" : "100%"}
          extensions={[html()]}
          theme={oneDark}
          onChange={onChange}
          className="h-full overflow-auto"
          basicSetup={{
            lineNumbers: true,
            highlightActiveLineGutter: true,
            highlightSpecialChars: true,
            foldGutter: true,
            drawSelection: true,
            dropCursor: true,
            allowMultipleSelections: true,
            indentOnInput: true,
            syntaxHighlighting: true,
            bracketMatching: true,
            closeBrackets: true,
            autocompletion: true,
            rectangularSelection: true,
            crosshairCursor: true,
            highlightActiveLine: true,
            highlightSelectionMatches: true,
            closeBracketsKeymap: true,
            defaultKeymap: true,
            searchKeymap: true,
            historyKeymap: true,
            foldKeymap: true,
            completionKeymap: true,
            lintKeymap: true,
          }}
        />
      </div>
      {!hideControls && (
        <div
          className={`absolute ${
            isExpanded ? "top-4 right-4 md:top-6 md:right-6" : "top-2 right-2"
          } flex gap-2 z-10`}
        >
          <button
            onClick={handleCopy}
            className={`p-2 rounded-md transition-colors ${
              isCopied
                ? "bg-volt-blue text-volt-deep-blue"
                : "bg-volt-carbon text-gray-300 hover:bg-gray-700"
            }`}
            title={isCopied ? "Copied!" : "Copy code"}
          >
            {isCopied ? (
              <Check className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 rounded-md bg-volt-carbon text-gray-300 hover:bg-gray-700 transition-colors"
            title={isExpanded ? "Minimize" : "Maximize"}
          >
            {isExpanded ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
          </button>
        </div>
      )}
      {isExpanded && (
        <div className="absolute top-4 left-4 md:top-6 md:left-6">
          <button className="text-white hover:text-volt-green transition-colors">
            Press ESC to close
          </button>
        </div>
      )}
    </div>
  );
}
